'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';
import { ChevronDown, ChevronUp, ArrowRight, AlertCircle, Loader2, HelpCircle } from 'lucide-react';

type PageState = 'loading' | 'populated' | 'unavailable';

interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category: string;
}

const faqItems: FAQItem[] = [
  {
    id: 'faq-1',
    category: 'About Panorama',
    question: 'What is Panorama Towers?',
    answer: 'Panorama Towers is a landmark twin high-rise residential community located at 4525 and 4575 Dean Martin Drive in Las Vegas, Nevada. Designed by Klai Juba Wald and recognized with an AIA Merit Award, the two 33-story glass towers are home to approximately 659 residences across tower units and townhomes. Panorama is one of the most architecturally distinctive residential addresses in Las Vegas.',
  },
  {
    id: 'faq-2',
    category: 'About Panorama',
    question: 'Is Panorama a good second home?',
    answer: 'Panorama is exceptionally well-suited for second-home ownership. The building\'s staffing, valet service, concierge team, and guarded entry make it easy to lock up and leave — and easy to return to. Many Panorama residents use it as a Las Vegas base for business, entertainment, or leisure. The lock-and-leave lifestyle is not an afterthought here — it\'s a feature.',
  },
  {
    id: 'faq-3',
    category: 'About Panorama',
    question: 'Are there townhomes as well as tower units?',
    answer: 'Yes. Panorama includes both tower residences and a collection of townhomes. Townhomes offer a distinct living experience — private entry, multi-level layouts, and a different relationship with the building — while still providing full access to all shared community amenities. They represent a compelling alternative for buyers who want more space or a ground-level lifestyle within a premium community.',
  },
  {
    id: 'faq-4',
    category: 'Amenities',
    question: 'What amenities are available at Panorama?',
    answer: 'Panorama offers a comprehensive amenity package including dual pools, fitness centers, spa facilities, massage rooms, valet service, a staffed guard gate, concierge support, and distinctive lobby and lounge areas. The building also includes resident storage and bike storage. The amenity experience is designed to support a premium, convenient lifestyle — whether you\'re full-time, part-time, or using Panorama as a base.',
  },
  {
    id: 'faq-5',
    category: 'Amenities',
    question: 'Is there valet at Panorama?',
    answer: 'Yes. Panorama offers valet-supported convenience for residents and guests. Valet service removes the friction of daily arrivals and departures — a meaningful quality-of-life upgrade that residents consistently cite as one of the building\'s best features.',
  },
  {
    id: 'faq-6',
    category: 'Lifestyle',
    question: 'What is the lifestyle like at Panorama?',
    answer: 'Panorama offers a premium, community-oriented lifestyle that blends high-rise convenience with genuine social connection. Residents have access to world-class amenities, a private community network, and a location that puts all of Las Vegas within reach. Many residents participate in community outings — Golden Knights games, Sphere events, dining outings, and social meetups. The building has a real culture, not just a list of features.',
  },
  {
    id: 'faq-7',
    category: 'Lifestyle',
    question: 'What is the location like?',
    answer: 'Panorama sits on Dean Martin Drive, approximately one block from the Las Vegas Strip. Harry Reid International Airport is about 10 minutes away. T-Mobile Arena (home of the Golden Knights) is about 5 minutes away. The Las Vegas Convention Center is about 8 minutes away. The Sphere is about 12 minutes away. The location offers urban convenience with the privacy of a guard-gated tower community.',
  },
  {
    id: 'faq-8',
    category: 'Community Platform',
    question: 'Is there a resident community platform?',
    answer: 'Yes. The Panorama Community Network is a private, resident-operated platform for connecting with neighbors, discovering trusted services, accessing community updates, and participating in Panorama life. It includes a resident directory, conversations, events, marketplace, newsletter, and resources. The platform is voluntary, unofficial, and not affiliated with building management or the HOA.',
  },
  {
    id: 'faq-9',
    category: 'Community Platform',
    question: 'Can renters join the community platform?',
    answer: 'Yes. The platform is open to all verified Panorama residents — owners, renters, and second-home residents alike. Renters are full members of the community and have access to the same features as owners, with the exception of certain owner-specific content areas.',
  },
  {
    id: 'faq-10',
    category: 'Community Platform',
    question: 'Is the platform official?',
    answer: 'No. The Panorama Community Network is resident-operated and not affiliated with Panorama Towers management, the HOA, or any official property entity. It exists because residents wanted a private, high-quality network for their community. It is unofficial, voluntary, and resident-built.',
  },
  {
    id: 'faq-11',
    category: 'Community Platform',
    question: 'How do I request access to the platform?',
    answer: 'You can request access through the Request Access page. Verification is straightforward — you\'ll confirm your residency at Panorama. Access may involve a resident vouch from a verified neighbor. Once approved, you\'ll have full access to the private community platform.',
  },
  {
    id: 'faq-12',
    category: 'Real Estate',
    question: 'How do I find out about buying or renting at Panorama?',
    answer: 'The Real Estate section of this platform provides an overview of ownership and rental opportunities at Panorama, including illustrative listing examples and a specialist contact area. For current availability and pricing, we recommend connecting with a Panorama-familiar real estate specialist. This platform does not list live MLS data.',
  },
];

const categories = ['All', ...Array.from(new Set(faqItems.map((f) => f.category)))];

function FAQAccordion({ item }: { item: FAQItem }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="neon-card overflow-hidden transition-all duration-200"
      style={open ? { border: '1px solid rgba(76,201,255,0.20)' } : {}}
    >
      <button
        className="w-full flex items-center justify-between p-6 text-left gap-4"
        onClick={() => setOpen(!open)}
      >
        <span className="font-display font-semibold text-[15px] leading-snug" style={{ color: '#F2F4FF' }}>
          {item.question}
        </span>
        <div
          className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-200"
          style={open
            ? { background: 'rgba(76,201,255,0.12)', border: '1px solid rgba(76,201,255,0.22)' }
            : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
        >
          {open
            ? <ChevronUp size={14} style={{ color: '#4CC9FF' }} />
            : <ChevronDown size={14} style={{ color: '#A7A3C4' }} />}
        </div>
      </button>
      {open && (
        <div className="px-6 pb-6">
          <div className="h-px mb-4" style={{ background: 'rgba(255,255,255,0.06)' }} />
          <p className="text-[14px] leading-relaxed" style={{ color: '#A7A3C4' }}>
            {item.answer}
          </p>
        </div>
      )}
    </div>
  );
}

export default function FAQPage() {
  const [pageState, setPageState] = useState<PageState>('loading');
  const [activeCategory, setActiveCategory] = useState<string>('All');

  useEffect(() => {
    const t = setTimeout(() => setPageState('populated'), 700);
    return () => clearTimeout(t);
  }, []);

  const visibleItems = activeCategory === 'All'
    ? faqItems
    : faqItems.filter((f) => f.category === activeCategory);

  return (
    <div className="min-h-screen" style={{ background: '#07060C' }}>
      <SiteHeader theme="transparent" />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-20 overflow-hidden panorama-hero-bg">
          <div
            className="absolute top-0 right-0 w-[55%] h-[75%] pointer-events-none"
            style={{ background: 'radial-gradient(ellipse 70% 65% at 100% 5%, rgba(122,92,255,0.18) 0%, rgba(76,201,255,0.08) 50%, transparent 70%)' }}
          />
          <div className="relative z-10 max-w-screen-2xl mx-auto px-6 lg:px-10">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 mb-6"
              style={{ borderRadius: '6px', background: 'rgba(122,92,255,0.10)', border: '1px solid rgba(122,92,255,0.22)' }}
            >
              <HelpCircle size={12} style={{ color: '#7A5CFF' }} />
              <span className="text-[12px] font-medium tracking-wide" style={{ color: '#7A5CFF' }}>
                Frequently Asked Questions
              </span>
            </div>
            <h1
              className="font-display font-extrabold text-[48px] sm:text-[60px] lg:text-[68px] leading-[1.04] tracking-tight mb-5 max-w-3xl"
              style={{ color: '#F2F4FF' }}
            >
              Questions about{' '}
              <span className="text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(135deg, #7A5CFF 0%, #4CC9FF 100%)' }}>
                Panorama?
              </span>
            </h1>
            <p className="text-[18px] leading-relaxed max-w-2xl mb-10 font-light" style={{ color: 'rgba(242,244,255,0.72)' }}>
              Common questions from prospective residents, visitors, and anyone curious about Panorama Towers and the resident community platform.
            </p>
          </div>
          <div className="absolute bottom-0 left-0 right-0 neon-divider" />
        </section>

        {/* Loading */}
        {pageState === 'loading' && (
          <section className="py-24" style={{ background: '#0C0A16' }}>
            <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 flex flex-col items-center justify-center py-20 gap-4">
              <Loader2 size={32} className="animate-spin" style={{ color: '#7A5CFF' }} />
              <p className="text-[15px]" style={{ color: '#A7A3C4' }}>Loading…</p>
            </div>
          </section>
        )}

        {/* Unavailable */}
        {pageState === 'unavailable' && (
          <section className="py-24" style={{ background: '#0C0A16' }}>
            <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 flex flex-col items-center justify-center py-20 gap-4 text-center">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,61,245,0.10)', border: '1px solid rgba(255,61,245,0.20)' }}>
                <AlertCircle size={22} style={{ color: '#FF3DF5' }} />
              </div>
              <h3 className="font-display font-semibold text-[20px]" style={{ color: '#F2F4FF' }}>Content unavailable</h3>
              <p className="text-[14px] max-w-sm" style={{ color: '#A7A3C4' }}>We're having trouble loading this page. Please try again shortly.</p>
              <Link href="/" className="btn-secondary text-[13px] px-4 py-2 mt-2">Return Home</Link>
            </div>
          </section>
        )}

        {/* Populated */}
        {pageState === 'populated' && (
          <>
            {/* Category filter */}
            <section style={{ background: '#0C0A16', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-4 overflow-x-auto">
                <div className="flex gap-2 min-w-max">
                  {categories.map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setActiveCategory(cat)}
                      className="px-3.5 py-1.5 rounded-lg text-[12px] font-medium transition-all duration-150"
                      style={activeCategory === cat
                        ? { background: 'rgba(122,92,255,0.12)', color: '#7A5CFF', border: '1px solid rgba(122,92,255,0.25)' }
                        : { background: 'rgba(255,255,255,0.04)', color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.07)' }}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>
            </section>

            {/* FAQ list */}
            <section className="py-16" style={{ background: '#0C0A16' }}>
              <div className="max-w-3xl mx-auto px-6 lg:px-10">
                <div className="space-y-3">
                  {visibleItems.map((item) => (
                    <FAQAccordion key={item.id} item={item} />
                  ))}
                </div>
              </div>
            </section>

            {/* CTA */}
            <section className="py-20" style={{ background: '#07060C' }}>
              <div className="neon-divider mb-0" />
              <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 pt-20 text-center">
                <h2 className="font-display font-bold text-[32px] sm:text-[40px] leading-tight tracking-tight mb-4" style={{ color: '#F2F4FF' }}>
                  Ready to learn more?
                </h2>
                <p className="text-[16px] max-w-xl mx-auto mb-8" style={{ color: '#A7A3C4' }}>
                  Explore the Panorama experience or request access to the private resident community.
                </p>
                <div className="flex flex-wrap items-center justify-center gap-4">
                  <Link href="/request-access" className="btn-primary group">
                    Request Access
                    <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-0.5" />
                  </Link>
                  <Link href="/why-panorama" className="btn-secondary">
                    Why Panorama
                  </Link>
                </div>

                {/* Cross-links */}
                <div
                  className="mt-14 pt-10 border-t"
                  style={{ borderColor: 'rgba(255,255,255,0.06)' }}
                >
                  <p className="text-[11px] font-semibold tracking-[0.10em] uppercase mb-5" style={{ color: 'rgba(167,163,196,0.35)' }}>
                    Explore more
                  </p>
                  <div className="flex flex-wrap items-center justify-center gap-3">
                    <Link href="/amenities" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                      Amenities
                    </Link>
                    <Link href="/lifestyle" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                      The Lifestyle
                    </Link>
                    <Link href="/community" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                      The Community
                    </Link>
                    <Link href="/real-estate" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                      Real Estate
                    </Link>
                    <Link href="/resources" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                      Resources
                    </Link>
                  </div>
                </div>
              </div>
            </section>
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
